import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import type { BadgeTon } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { IconAbrechnungen, IconHaken, IconHinweis, IconObjekte, IconSchild, IconWartung, IconZaehler } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { demoStartseite, type StartseiteDaten, type Ampel } from '../lib/startseite-daten';
import { formatDatum, formatEuro } from '../lib/format';

const AMPEL_TON: Record<Ampel, BadgeTon> = { rot: 'gefahr', gelb: 'warnung', gruen: 'erfolg' };
const AMPEL_TEXT: Record<Ampel, string> = { rot: 'Überfällig', gelb: 'Bald fällig', gruen: 'Im Plan' };

const VERBINDLICHKEIT_TEXT: Record<string, string> = {
  gesetz: 'Gesetzliche Pflicht',
  regel_der_technik: 'Regel der Technik',
  vertraglich: 'Vertraglich',
  empfehlung: 'Empfehlung',
};

export interface StartseiteProps {
  onOeffneAbrechnungen?: () => void;
  onOeffneZaehler?: (objektId: string) => void;
}

export function Startseite({ onOeffneAbrechnungen, onOeffneZaehler }: StartseiteProps) {
  const [daten, setDaten] = useState<StartseiteDaten | null>(DEMO ? demoStartseite() : null);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [verbrauch, setVerbrauch] = useState<{ anzahl: number; objektId: string | null }>(
    DEMO ? { anzahl: 2, objektId: 'obj-1' } : { anzahl: 0, objektId: null },
  );
  const [forderungen, setForderungen] = useState<{ gesamtOffenCent: number; anzahlImRueckstand: number }>(
    DEMO ? { gesamtOffenCent: 96000, anzahlImRueckstand: 1 } : { gesamtOffenCent: 0, anzahlImRueckstand: 0 },
  );

  useEffect(() => {
    if (DEMO) return;
    let aktiv = true;
    setLaden(true);
    api
      .get<StartseiteDaten>('/startseite')
      .then((d) => aktiv && (setDaten(d), setFehler(null)))
      .catch((e) => aktiv && setFehler(e instanceof ApiFehler ? e.message : 'Die Übersicht konnte nicht geladen werden.'))
      .finally(() => aktiv && setLaden(false));
    api
      .get<{ anzahl: number; faellige: { objektId: string }[] }>('/zaehler/faellige')
      .then((v) => aktiv && setVerbrauch({ anzahl: v.anzahl, objektId: v.faellige[0]?.objektId ?? null }))
      .catch(() => {});
    api
      .get<{ gesamtOffenCent: number; anzahlImRueckstand: number }>('/zahlungen/forderungen')
      .then((f) => aktiv && setForderungen(f))
      .catch(() => {});
    return () => {
      aktiv = false;
    };
  }, []);

  return (
    <div>
      <Seitenkopf
        titel="Übersicht"
        beschreibung="Was heute zählt: fällige Prüf- und Wartungsfristen, laufende Abrechnungsfristen und gesetzliche Stichtage — auf einen Blick über alle Objekte."
      />

      {laden && !daten ? (
        <p className="text-sm text-fg-muted">Übersicht wird geladen …</p>
      ) : fehler && !daten ? (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      ) : daten ? (
        <Inhalt
          daten={daten}
          onOeffneAbrechnungen={onOeffneAbrechnungen}
          verbrauch={verbrauch}
          onOeffneZaehler={onOeffneZaehler}
          forderungen={forderungen}
        />
      ) : null}
    </div>
  );
}

function Inhalt({
  daten,
  onOeffneAbrechnungen,
  verbrauch,
  onOeffneZaehler,
  forderungen,
}: {
  daten: StartseiteDaten;
  onOeffneAbrechnungen?: () => void;
  verbrauch: { anzahl: number; objektId: string | null };
  onOeffneZaehler?: (objektId: string) => void;
  forderungen: { gesamtOffenCent: number; anzahlImRueckstand: number };
}) {
  const { pruefpflichten, abrechnungsfristen, stichtage, zusammenfassung } = daten;
  const rot = pruefpflichten.zusammenfassung.rot;
  const gelb = pruefpflichten.zusammenfassung.gelb;

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Kennzahl
          icon={<IconSchild groesse={18} />}
          wert={String(rot)}
          label="Überfällige Prüffristen"
          ton={rot > 0 ? 'gefahr' : 'erfolg'}
          hinweis={gelb > 0 ? `${gelb} bald fällig` : 'nichts Weiteres offen'}
        />
        <Kennzahl
          icon={<IconAbrechnungen groesse={18} />}
          wert={String(abrechnungsfristen.offen)}
          label="Offene Abrechnungen"
          ton={abrechnungsfristen.kritisch > 0 ? 'warnung' : 'neutral'}
          hinweis={
            abrechnungsfristen.kritisch > 0
              ? `${abrechnungsfristen.kritisch} mit knapper Frist`
              : 'Fristen im Rahmen'
          }
        />
        <Kennzahl
          icon={<IconObjekte groesse={18} />}
          wert={String(zusammenfassung.objekte)}
          label="Objekte"
          ton="neutral"
          hinweis={
            zusammenfassung.offeneAbrechnungen === 1
              ? '1 laufender Abrechnungslauf'
              : `${zusammenfassung.offeneAbrechnungen} laufende Abrechnungsläufe`
          }
        />
        <Kennzahl
          icon={<IconWartung groesse={18} />}
          wert={String(zusammenfassung.anlagen)}
          label="Anlagen im Buch"
          ton="neutral"
          hinweis={
            zusammenfassung.offenePflichten === 1
              ? '1 aktive Pflicht'
              : `${zusammenfassung.offenePflichten} aktive Pflichten`
          }
        />
        {verbrauch.objektId && onOeffneZaehler ? (
          <button className="min-w-0 text-left" onClick={() => onOeffneZaehler(verbrauch.objektId!)}>
            <Kennzahl
              icon={<IconZaehler groesse={18} />}
              wert={String(verbrauch.anzahl)}
              label="Verbrauchsinfo fällig"
              ton={verbrauch.anzahl > 0 ? 'warnung' : 'erfolg'}
              hinweis="monatlich, fernablesbar"
            />
          </button>
        ) : (
          <Kennzahl
            icon={<IconZaehler groesse={18} />}
            wert={String(verbrauch.anzahl)}
            label="Verbrauchsinfo fällig"
            ton={verbrauch.anzahl > 0 ? 'warnung' : 'erfolg'}
            hinweis="monatlich, fernablesbar"
          />
        )}
        <Kennzahl
          icon={<IconAbrechnungen groesse={18} />}
          wert={formatEuro(forderungen.gesamtOffenCent)}
          label="Offene Forderungen"
          ton={forderungen.gesamtOffenCent > 0 ? 'warnung' : 'erfolg'}
          hinweis={forderungen.anzahlImRueckstand > 0 ? `${forderungen.anzahlImRueckstand} im Rückstand` : 'alles ausgeglichen'}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <Card
            titel="Fällige Prüf- und Wartungsfristen"
            beschreibung="Automatisch aus dem Anlagenbuch abgeleitet. Rot ist überfällig, Gelb wird bald fällig."
            ohnePolster
          >
            {pruefpflichten.faellig.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon={<IconHaken groesse={22} />}
                  titel="Alles im grünen Bereich"
                  beschreibung="Zurzeit sind keine Prüf- oder Wartungsfristen überfällig oder unmittelbar fällig."
                />
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {pruefpflichten.faellig.map((p) => (
                  <li key={p.id} className="flex items-start justify-between gap-4 px-4 py-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-fg">{p.bezeichnung}</span>
                        <Badge ton={AMPEL_TON[p.ampel]} punkt>
                          {AMPEL_TEXT[p.ampel]}
                        </Badge>
                      </div>
                      <p className="mt-1 truncate text-sm text-fg-muted">
                        {p.objektBezeichnung} · {p.anlageBezeichnung}
                      </p>
                      <p className="mt-1 text-xs text-fg-subtle">
                        {p.verbindlichkeit ? VERBINDLICHKEIT_TEXT[p.verbindlichkeit] ?? p.verbindlichkeit : 'Pflicht'}
                        {p.rechtsgrundlage ? ` · ${p.rechtsgrundlage}` : ''}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-medium text-fg">
                        {p.naechsteFaellig ? formatDatum(p.naechsteFaellig) : 'Termin offen'}
                      </p>
                      <p className="mt-0.5 text-xs text-fg-subtle">{fristText(p.tageBisFaellig)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card
            titel="Abrechnungsfristen"
            beschreibung="§ 556 Abs. 3 BGB: zwölf Monate nach Ende des Abrechnungszeitraums."
            aktion={
              onOeffneAbrechnungen && (
                <button
                  type="button"
                  onClick={onOeffneAbrechnungen}
                  className="text-sm font-medium text-accent hover:underline"
                >
                  Öffnen
                </button>
              )
            }
            ohnePolster
          >
            {abrechnungsfristen.liste.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon={<IconHaken groesse={22} />}
                  titel="Keine offenen Läufe"
                  beschreibung="Alle Abrechnungen sind festgeschrieben oder versendet."
                />
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {abrechnungsfristen.liste.map((a) => (
                  <li key={a.id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-sm font-semibold text-fg">{a.objektBezeichnung}</span>
                      <Badge ton={AMPEL_TON[(a.ampel as Ampel) ?? 'gruen']} punkt>
                        {a.tageBisFrist !== null && a.tageBisFrist < 0
                          ? 'Frist verstrichen'
                          : `${a.tageBisFrist ?? '–'} Tage`}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs text-fg-subtle">
                      {a.fristBis ? `Frist bis ${formatDatum(a.fristBis)}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card titel="Gesetzliche Stichtage" ohnePolster>
            {stichtage.length === 0 ? (
              <div className="p-4">
                <EmptyState titel="Keine anstehenden Stichtage" />
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {stichtage.map((s) => (
                  <li key={`${s.katalogCode}-${s.stichtag}`} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-sm font-semibold text-fg">{s.bezeichnung}</span>
                      <span className="shrink-0 text-xs text-fg-subtle">
                        {s.stichtag ? formatDatum(s.stichtag) : ''}
                      </span>
                    </div>
                    {s.folge && <p className="mt-1 text-xs text-fg-muted">{s.folge}</p>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

function Kennzahl({
  icon,
  wert,
  label,
  hinweis,
  ton,
}: {
  icon: ReactNode;
  wert: string;
  label: string;
  hinweis: string;
  ton: BadgeTon;
}) {
  const akzent =
    ton === 'gefahr'
      ? 'text-danger'
      : ton === 'warnung'
        ? 'text-warning-soft-fg'
        : ton === 'erfolg'
          ? 'text-success'
          : 'text-fg';
  return (
    <Card>
      <div className="flex items-start gap-2 text-fg-subtle">
        <span aria-hidden="true" className="shrink-0">{icon}</span>
        <span className="min-w-0 break-words text-xs font-medium uppercase tracking-wide [hyphens:auto]">{label}</span>
      </div>
      <p className={'mt-2 break-words text-xl font-semibold tabellenziffern sm:text-2xl ' + akzent}>{wert}</p>
      <p className="mt-0.5 text-xs text-fg-subtle">{hinweis}</p>
    </Card>
  );
}

function fristText(tage: number | null): string {
  if (tage === null) return 'Termin offen';
  if (tage < 0) return `seit ${Math.abs(tage)} Tagen überfällig`;
  return `in ${tage} Tagen`;
}

export default Startseite;
